import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';

interface AuthContextType {
  isAdmin: boolean;
  adminUsername: string;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  changePassword: (username: string, oldPassword: string, newPassword: string) => Promise<{ success: boolean; message: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [adminUsername, setAdminUsername] = useState<string>('');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const username = localStorage.getItem('adminUsername');
    if (token) {
      setIsAdmin(true);
      setAdminUsername(username || 'Admin');
    }
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const result = await api.login(username, password);
      if (result.success) {
        setIsAdmin(true);
        setAdminUsername(username);
        localStorage.setItem('adminToken', result.token);
        localStorage.setItem('adminUsername', username);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    setIsAdmin(false);
    setAdminUsername('');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
  };

  const changePassword = async (
    username: string, 
    oldPassword: string, 
    newPassword: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const result = await api.changePassword(username, oldPassword, newPassword);
      if (result.success) {
        // Password changed - logout user
        logout();
        return { success: true, message: 'Password changed successfully. Please login again.' };
      }
      return { success: false, message: result.error || 'Failed to change password' };
    } catch (error: any) {
      return { success: false, message: error.message || 'Network error' };
    }
  };

  return (
    <AuthContext.Provider value={{ isAdmin, adminUsername, login, logout, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};